import React, { useState, useEffect, useCallback } from "react";
import { IEventSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment";

import { apiService } from "src/services/api.service";

import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import { download } from "src/components/Download/DownloadManager";
import { Modal, useModal } from "src/components/Modal/Modal";
import RefundAll from "src/components/RefundAll/RefundAll";

import type { ColDef } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { ActionsRenderer } from "./ActionsRenderer";

import styles from "./TransactionsTab.module.scss";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";
import { GridApi } from "ag-grid-community";
import { useDebounce } from "src/hooks/useDebounce";

interface IProps {
  event: IEventSummary,
  currentTab: number,
  tabIndex: number
}

interface IRow {
  date: string,
  totalEntryAmount: string,
  discount: string,
  totalAmount: string,
  totalFees: string,
  totalCharged: string,
  clubAmt: string,
  refundedAmt: string,
  discountCode: string,
  paymentType: string,
  status: string,
  paymentMethodType: string,
  paymentFailureReason: string,
  disputeReason: string,
  name: string,
  email: string,
  hasOverride: boolean,
  overrideNote: string,
  note: string,
  actions: any
}

const TransactionsTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(true);
  const [gridApi, setGridApi] = useState<GridApi<IRow>>();
  const [ownerNameSearch, setOwnerNameSearch] = useState<string>("");
  const debouncedOwnerNameSearch = useDebounce<string>(ownerNameSearch, 800);
  const [emailSearch, setEmailSearch] = useState<string>("");
  const debouncedEmailSearch = useDebounce<string>(emailSearch, 800);
  const [rawTransactions, setRawTransactions] = useState<any[]>([]);
  const { isShown: refundAllShown, toggle: toggleRefundAll } = useModal();

  const resetFilters = useCallback(() => {
    if (gridApi) {
      setOwnerNameSearch("");
      setEmailSearch("");
      gridApi.setFilterModel(null);
    }
  }, [gridApi]);

  useEffect(() => {
    let filters: any = {};
    if (gridApi) {
      if (!!debouncedEmailSearch) {
        filters.email = {
          filterType: "text",
          type: "contains",
          filter: debouncedEmailSearch,
        };
      } else {
        filters.email = undefined;
      }
      if (!!debouncedOwnerNameSearch) {
        filters.name = {
          filterType: "text",
          type: "contains",
          filter: debouncedOwnerNameSearch,
        };
      } else {
        filters.name = undefined;
      }
      gridApi.setFilterModel(filters);
    }
  }, [gridApi, debouncedOwnerNameSearch, debouncedEmailSearch]);

  const menuActions:IContextMenuOption[] = [
    {
      label: "Export Transactions",
      onClick: () => {
        download.pushDownload({
          type: "transactions",
          eventId: props.event._id
        });
      }
    },
    {
      label: "Refund All",
      onClick: () => {
        toggleRefundAll();
      }
    }
  ];

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto"
  });

  const formatPaymentType = (type: string): string => {
    switch (type) {
      case "pay_club_directly": return "Pay Club Directly";
      case "card": return "Credit Card";
      case "bank_account": return "Bank Account";
      case "cash": return "Cash";
      case "cheque": return "Cheque";
      case "e_transfer": return "E-Transfer";
      default: return type || "";
    }
  };

  const getTransactions = useCallback((id: string) => {
    let query:any = {
      sort: [{
        attribute_name: "created_at",
        sort: "desc"
      }],
      query: {
        $and: [{
          attribute_name: "status",
          attribute_value: {
            operator: "$in",
            value: ["success", "unpaid", "cancelled", "payment_requested", "processing"]
          }
        }]
      }
    };

    apiService.getEventTransactions(id, query).then((response) => {
      let rows:IRow[] = [];

      setRawTransactions(response);

      for (let i in response) {
        let row:any = response[i];

        let formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: row.amounts.currency.toUpperCase(),
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });

        const totalFees = (row.amounts.calculated_application_fee || row.amounts.processed_application_fee || 0) + (row.amounts.calculated_processing_fee || 0) + (row.amounts.calculated_processing_tax || 0);
        const clubAbsorbsFees = row.amounts.platform_fees_by === Constants.platform_fees_by.club;
        const entryAmount = clubAbsorbsFees
          ? (row.amounts.calculated_total_amount || 0)
          : (row.amounts.calculated_base_amount || 0);
        const discountAmount = row.amounts.calculated_discount_amount || 0;
        const totalAmountAfterDiscount = entryAmount - discountAmount;
        const isBankAccountSettling =
          row.amounts.payment_method_type === Constants.payment_method_type.bank_account &&
          row.status === "processing";
        const totalChargedAmount = isBankAccountSettling
          ? (row.amounts.calculated_total_amount || 0)
          : (row.amounts.processed_total_amount || 0);
        
        const rowHasOverride = !!row.override_note ||
          (row.dog_show_registrations || []).some((dsr: any) =>
            (dsr.dogs || []).some((dog: any) =>
              (dog.dog_classes || []).some((cls: any) => cls.fee_override !== undefined)
            )
          );

        rows.push({
          date: moment(row.created_at).format("MM/DD/YY hh:mm:ssA"),
          totalEntryAmount: formatter.format((entryAmount / 100) as number),
          discount: formatter.format((discountAmount / 100) as number),
          totalAmount: formatter.format((totalAmountAfterDiscount / 100) as number),
          totalFees: formatter.format((totalFees / 100) as number),
          totalCharged: formatter.format((totalChargedAmount / 100) as number),
          clubAmt: formatter.format(((isBankAccountSettling ? (row.amounts.calculated_base_amount || 0) : (row.amounts.processed_amount || 0)) / 100) as number),
          refundedAmt: formatter.format((row.amounts.refunded_amount ? row.amounts.refunded_amount / 100 : 0) as number),
          discountCode: row.discount_code || "",
          paymentType: formatPaymentType(row.amounts.payment_method_type),
          status: row.status || "",
          paymentMethodType: row.amounts.payment_method_type || "",
          paymentFailureReason: row.payment_failed_at
            ? (row.payment_failure_reason || "Bank payment was returned")
            : "",
          disputeReason: row.dispute_reason
            ? (row.payment_failure_reason || `Payment disputed (${row.dispute_reason})`)
            : (row.payment_failure_reason || ""),
          name: row.user.first_name + " " + row.user.last_name,
          email: row.user.email,
          hasOverride: rowHasOverride,
          overrideNote: row.override_note || "",
          note: row.payment_request_note || "",
          actions: {
            transaction: row,
            event: props.event
          }
        });
      }

      setRowData(rows);
    });
  }, [props.event]);

  const StatusCellRenderer = (params: any) => {
    const val: string = params.value || "";
    const paymentMethodType: string = params.data?.paymentMethodType || "";
    const paymentFailureReason: string = params.data?.paymentFailureReason || "";
    const disputeReason: string = params.data?.disputeReason || "";
    if (val === "disputed") {
      return (
        <span
          className={styles.statusDisputed}
          title={disputeReason || "This payment is being disputed. Funds are typically withdrawn until the dispute is resolved."}
        >
          Disputed
        </span>
      );
    }
    if (val === "dispute_lost") {
      return (
        <span
          className={styles.statusDisputeLost}
          title={disputeReason || "The dispute was lost or the charge was refunded to close it."}
        >
          Dispute Lost
        </span>
      );
    }
    if (val === "unpaid" && paymentFailureReason) {
      // Bank debit returned after the entries were created: the club needs to
      // collect directly and Mark as Paid, or cancel the entries.
      return (
        <span
          className={styles.statusUnpaid}
          title={`Bank payment returned: ${paymentFailureReason}. Collect payment directly and use "Mark as Paid", or "Cancel Entry".`}
        >
          Unpaid – Payment Returned
        </span>
      );
    }
    if (val === "unpaid") return <span className={styles.statusUnpaid}>Unpaid</span>;
    if (val === "cancelled") return <span className={styles.statusCancelled}>Cancelled</span>;
    if (val === "payment_requested") return <span className={styles.statusPaymentRequested}>Awaiting Payment</span>;
    if (val === "processing" && paymentMethodType === Constants.payment_method_type.bank_account) {
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span className={styles.statusSuccess}>Paid</span>
          <span className={styles.statusSettling} title="Payment authorized — bank transfer settling in 3-5 business days" />
        </span>
      );
    }
    if (val === "processing") return <span className={styles.statusProcessing}>Processing</span>;
    if (val === "failed") return <span className={styles.statusUnpaid}>Failed</span>;
    if (val === "pending") return <span className={styles.statusProcessing}>Pending</span>;
    if (val === "success") return <span className={styles.statusSuccess}>Paid</span>;
    return <span className={styles.statusSuccess}>Paid</span>;
  };

  const EntryAmountCellRenderer = (params: any) => {
    const hasOverride: boolean = params.data?.hasOverride;
    const note: string = params.data?.overrideNote;
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {params.value}
        {hasOverride && (
          <span title={note ? `Override note: ${note}` : "Price override applied"} style={{ color: "#e65100", fontWeight: 700, cursor: "default", fontSize: "24px", lineHeight: 1 }}>⚠</span>
        )}
      </span>
    );
  };

  let colDefs:ColDef<IRow>[] = [
    { field: "date", headerName: "Date/Time", width: 180, minWidth: 180, maxWidth: 180, suppressSizeToFit: true, filter: true },
    { field: "paymentType", headerName: "Payment Type", width: 170, minWidth: 170, filter: true },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      minWidth: 130,
      filter: true,
      cellRenderer: StatusCellRenderer,
      cellStyle: { display: "flex", alignItems: "center" }
    },
    {
      field: "totalEntryAmount",
      headerName: "Entry Amount",
      width: 160,
      minWidth: 130,
      filter: true,
      cellRenderer: EntryAmountCellRenderer,
      cellStyle: { display: "flex", alignItems: "center" }
    },
    { field: "discount", headerName: "Discount", width: 130, minWidth: 120, filter: true },
    { field: "totalAmount", headerName: "Total Amount", width: 170, minWidth: 130, filter: true },
    { field: "totalFees", headerName: "Total Fees", width: 150, minWidth: 120, filter: true },
    { field: "totalCharged", headerName: "Total Charged", width: 170, minWidth: 130, filter: true },
    { field: "clubAmt", headerName: "Club Amt", width: 130, minWidth: 120, filter: true },
    { field: "refundedAmt", headerName: "Refunded", width: 130, minWidth: 120, filter: true },
    { field: "discountCode", headerName: "Discount Code", width: 150, minWidth: 130, filter: true },
    { field: "name", headerName: "Name", minWidth: 120, filter: "agTextColumnFilter" },
    { field: "email", headerName: "Email", minWidth: 180, filter: "agTextColumnFilter" },
    { field: "note", headerName: "Note", minWidth: 180, filter: "agTextColumnFilter" },
    {
      headerName: "",
      field: "actions",
      maxWidth: 50,
      minWidth: 50,
      cellRenderer: ActionsRenderer,
      pinned: "right",
      cellRendererParams: {
        getTransactions: () => { getTransactions(props.event._id); }
      },
      resizable: true,
      cellStyle: {
        "overflow": "visible",
        "zIndex": "2"
      }
    }
  ];

  const [rowData, setRowData] = useState<IRow[]>([]);
  const hasBankAccountSettling = rowData.some(
    (r) => r.status === "processing" && r.paymentMethodType === Constants.payment_method_type.bank_account
  );

  const defaultColDef: ColDef = {
    //flex: 1
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  useEffect(() => {
    if (props.currentTab === props.tabIndex) {
      getTransactions(props.event._id);
    }
  }, [props.currentTab, props.tabIndex, props.event._id, getTransactions]);

  useEffect(() => {
    if (!loaded) {
      getTransactions(props.event._id);
      setLoaded(true);
    }
  }, [loaded, getTransactions, props.event._id]);

  return (
    <div className={styles.TransactionsTab}>
      <Modal
        className="refundAllModal"
        isShown={refundAllShown}
        hide={toggleRefundAll}
        modalContent={
          <RefundAll
            transactions={rawTransactions}
            eventId={props.event._id}
            onFinish={() => {
              toggleRefundAll();
              getTransactions(props.event._id);
            }}
            onCancel={toggleRefundAll}
          />
        }
      />
      <div className={styles.actions}>
        <div className={styles.left}>
          <Input
            className={styles.textInput}
            autoComplete="off"
            type="text"
            label="Owner Name"
            value={ownerNameSearch}
            onChange={(e) => { setOwnerNameSearch(e.target.value); }}
          />
          <Input
            className={styles.textInput}
            autoComplete="off"
            type="email"
            label="Owner Email"
            value={emailSearch}
            onChange={(e) => { setEmailSearch(e.target.value); }}
          />
          <Button disabled={!(ownerNameSearch || emailSearch)} className={styles.reset} onClick={() => { resetFilters(); }}>Reset</Button>
        </div>
        <div className={styles.right}>
        <ContextMenu
          label={"Actions"}
          options={menuActions}
        />
        </div>
      </div>
      <div className={styles.tableWrap}>
        {hasBankAccountSettling && (
          <div className={styles.legend}>
            <span className={styles.statusSettling} />
            <span>Bank transfer authorized — funds settling in 3-5 business days</span>
          </div>
        )}
        <AgGridReact
          rowData={rowData}
          suppressRowTransform={true}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          theme={theme}
          pagination={true}
          paginationPageSize={20}
          onGridReady={onGridReady}
          getRowStyle={(params: any) => {
            if (params.data?.status === "unpaid") return { background: "#FFF7ED", color: "#000" };
            if (params.data?.status === "disputed") return { background: "#FEF2F2", color: "#000" };
            if (params.data?.status === "dispute_lost") return { background: "#FEE2E2", color: "#000" };
            if (params.data?.status === "cancelled") return { background: "#F0F0F0", color: "#9CA3AF" };
            if (params.data?.status === "payment_requested") return { background: "#FFF6EB", color: "#F25A2D" };
            if (params.data?.status === "processing") return { background: "#EFF6FF", color: "#000" };
            return undefined;
          }}
        />
      </div>
    </div>
  );
};

export default TransactionsTab;